import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipomodalidade.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';
import { useNavigate } from 'react-router-dom';

const TipoModalidadeManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_tp_modalidade: '',
    descr_modalidade: '',
    sigla_tp_modalidade: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);
  const [proximoId, setProximoId] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'tipo de modalidade', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/tipo-modalidade/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'tipo de modalidade', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de modalidade');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);


  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'tipo de modalidade', { id });
    try {
      const response = await api.delete(`/tipo-modalidade/${id}`);
      logarSucesso('Excluir', 'tipo de modalidade', response.data);
      setDados(currentData => currentData.filter(item => item.id_tp_modalidade !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de modalidade');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const colunasBase = [
      {
        title: 'ID',
        field: 'id_tp_modalidade',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Descrição',
        field: 'descr_modalidade',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      },
      {
        title: 'Sigla',
        field: 'sigla_tp_modalidade',
        headerFilter: 'input',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_tp_modalidade, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_tp_modalidade}"?`)) {
            excluirRegistro(data.id_tp_modalidade);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.field === 'id_tp_modalidade' ? undefined : 'input'
      }))
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_tp_modalidade'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-modalidade');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-modalidade');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchNextId();
  }, [verificarAPI, fetchNextId]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { descr_modalidade, sigla_tp_modalidade } = formData;

    // Validação de campos obrigatórios (ID é gerado automaticamente)
    const camposObrigatorios = [];
    const erros = [];

    if (!descr_modalidade) {
      camposObrigatorios.push('Descrição');
      erros.push('descr_modalidade');
    }
    if (!sigla_tp_modalidade) {
      camposObrigatorios.push('Sigla');
      erros.push('sigla_tp_modalidade');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    // Sempre envia 'AUTO' para o backend gerar o ID automaticamente
    const dadosParaEnviar = {
      id_tp_modalidade: 'AUTO',
      descr_modalidade,
      sigla_tp_modalidade
    };

    logarRequisicao('Adicionar', 'tipo de modalidade', dadosParaEnviar);
    try {
      setLoading(true);
      const response = await api.post('/tipo-modalidade', dadosParaEnviar);
      logarSucesso('Adicionar', 'tipo de modalidade', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_tp_modalidade: 'AUTO', descr_modalidade: '', sigla_tp_modalidade: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      fetchNextId();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de modalidade');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Remove o erro do campo quando ele for preenchido
    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipos_modalidade.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Modalidade</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_tipo_modalidade</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/modalidade.mov" buttonText="Ver Tutorial Modalidade" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_tp_modalidade">ID:</label>
            <input type="text" id="id_tp_modalidade" name="id_tp_modalidade" value="" placeholder="Gerado ao adicionar" disabled={true} style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }} title="ID gerado automaticamente pelo sistema" />
          </div>
          <div className="form-group">
            <label htmlFor="descr_modalidade">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="descr_modalidade" name="descr_modalidade" value={formData.descr_modalidade} onChange={handleInputChange} placeholder="Ex: Modalidade de Ensino" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('descr_modalidade') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="sigla_tp_modalidade">Sigla:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="sigla_tp_modalidade" name="sigla_tp_modalidade" value={formData.sigla_tp_modalidade} onChange={handleInputChange} placeholder="Ex: EM" maxLength={10} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('sigla_tp_modalidade') ? 'input-error' : ''} />
          </div>
          <div className="actions-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}>
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoModalidadeManager;